// Polls Spectrum for "is this session still good?" while the app is embedded.
//
// The proactive half of renewal plans against `expires_in`, which only covers
// the token dying of old age. A session can also end early — an idle timeout on
// Spectrum's side, an explicit revoke — and nothing in the frame would notice
// until a request failed. This is what notices.
//
// Contract (core-api OidcController::actionHeartbeat, renewal spec §5):
//
//   GET  {api}/application/api/u/iframe/heartbeat
//        Authorization: Bearer <the scoped embed token>
//   200  { "renew_required": true | false, "expires_in": <seconds> }
//
// `expires_in` is counted on Spectrum's clock, so the caller re-arms its own
// fallback timer from it on every poll — a drifting or sleeping client clock
// then does not matter. A poll is not recorded as user activity server-side.
//
// Polls only while the tab is visible, and once as soon as it becomes visible
// again — a laptop waking from sleep is exactly when a token may have died.
//
// Off unless VUE_APP_EMBED_HEARTBEAT_SECONDS is set to a positive number, so a
// build cannot start polling an endpoint an environment does not have yet.

const TIMEOUT_MS = 10000;

export type HeartbeatDeps = {
  apiBaseUrl: string;
  /** Read at each poll, not captured: the token changes under a renewal. */
  token: () => string;
  /** The server wants a new token — ask for one. */
  onRenewRequired: () => void;
  /** Seconds the current token has left, by the server's clock. */
  onExpiresIn?: (seconds: number) => void;
};

export const heartbeatIntervalMs = (): number => {
  const seconds = Number(process.env.VUE_APP_EMBED_HEARTBEAT_SECONDS ?? 0);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
};

/** Start polling. Returns a teardown; a no-op when polling is not configured. */
export const startHeartbeat = (deps: HeartbeatDeps): (() => void) => {
  const intervalMs = heartbeatIntervalMs();
  if (intervalMs === 0) return () => undefined;

  let stopped = false;

  const poll = async (): Promise<void> => {
    const token = deps.token();
    if (!token) return;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(
        `${deps.apiBaseUrl}/application/api/u/iframe/heartbeat`,
        {
          headers: { authorization: `Bearer ${token}` },
          signal: controller.signal,
          credentials: "omit",
        },
      );
      // A 401 is the same answer as `renew_required: true`, arriving by a
      // different route: the token this poll carried is no longer accepted.
      if (response.status === 401) {
        deps.onRenewRequired();
        return;
      }
      if (!response.ok) return;
      const body: unknown = await response.json();
      if (body && typeof body === "object") {
        const data = body as Record<string, unknown>;
        if (typeof data.expires_in === "number" && data.renew_required !== true) {
          deps.onExpiresIn?.(data.expires_in);
        }
        if (data.renew_required === true) deps.onRenewRequired();
      }
    } catch (e) {
      // A failed poll says nothing about the session — the network is the most
      // likely explanation, and acting on it would renew for no reason.
      // eslint-disable-next-line no-console
      console.warn("[embed] heartbeat poll failed", e);
    } finally {
      clearTimeout(timer);
    }
  };

  const visible = (): boolean =>
    typeof document === "undefined" || document.visibilityState !== "hidden";

  const interval = setInterval(() => {
    if (!stopped && visible()) void poll();
  }, intervalMs);

  const onVisibility = (): void => {
    if (!stopped && visible()) void poll();
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    stopped = true;
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
  };
};
